import {Box, Grid2 as Grid, Typography} from '@mui/material'
import type {SxProps, Theme} from '@mui/material'
import {useCallback, useEffect, useMemo, useState} from 'react'
import type {DocumentReference, QueryDocumentSnapshot} from 'firebase/firestore'
import {isCourseArchived, type Course} from '@goschool/model'
import {useFirestoreSnapshot} from '@goschool/react-firebase'
import {CourseCard} from './CourseCard'
import {CourseContextProvider} from '@goschool/dao'
import {useTranslation} from 'react-i18next'

export function CoursesGrid({courseRefs}: { courseRefs: DocumentReference<Course>[] }) {
  const {t} = useTranslation()
  const [snapshotByPath, setSnapshotByPath] = useState<Record<string, QueryDocumentSnapshot<Course> | null>>({})

  useEffect(
    () => {
      setSnapshotByPath(previousSnapshots => {
        const nextSnapshots: Record<string, QueryDocumentSnapshot<Course> | null> = {}
        courseRefs.forEach(({path}) => {
          nextSnapshots[path] = previousSnapshots[path] ?? null
        })
        return nextSnapshots
      })
    },
    [courseRefs]
  )

  const handleSnapshotChange = useCallback(
    (path: string, snapshot: QueryDocumentSnapshot<Course> | null) => {
      setSnapshotByPath(previousSnapshots => {
        if (previousSnapshots[path] === snapshot) {
          return previousSnapshots
        }
        return {...previousSnapshots, [path]: snapshot}
      })
    },
    []
  )

  const courseSnapshots = courseRefs
    .map(({path}) => snapshotByPath[path])
    .filter((snapshot): snapshot is QueryDocumentSnapshot<Course> => snapshot != null)
  const activeCourseSnapshots = courseSnapshots.filter(snapshot => !isCourseArchived(snapshot.data()))
  const archivedCourseSnapshots = courseSnapshots.filter(snapshot => isCourseArchived(snapshot.data()))

  if (courseRefs.length === 0) {
    return <Typography variant="body1">No courses, create one</Typography>
  }

  return <>
    {courseRefs.map(courseRef =>
      <CourseSnapshotLoader
        key={courseRef.path}
        courseRef={courseRef}
        onSnapshotChange={handleSnapshotChange}
      />
    )}
    <CourseSection
      title={t('course:catalog.active-courses')}
      courseSnapshots={activeCourseSnapshots}
    />
    {archivedCourseSnapshots.length > 0 &&
      <CourseSection
        title={t('course:catalog.archived-courses')}
        courseSnapshots={archivedCourseSnapshots}
        sx={{mt: 5}}
      />}
  </>
}


function CourseSnapshotLoader({
  courseRef,
  onSnapshotChange
}: {
  courseRef: DocumentReference<Course>
  onSnapshotChange: (path: string, snapshot: QueryDocumentSnapshot<Course> | null) => void
}) {
  const snapshot = useFirestoreSnapshot(courseRef)
  const coursePath = courseRef.path
  const existingSnapshot = useMemo(
    () => snapshot?.exists() ? snapshot : null,
    [snapshot]
  )

  useEffect(
    () => {
      onSnapshotChange(coursePath, existingSnapshot)
      return () => onSnapshotChange(coursePath, null)
    },
    [coursePath, existingSnapshot, onSnapshotChange]
  )

  return null
}

function CourseSection({
  title,
  courseSnapshots,
  sx
}: {
  title: string
  courseSnapshots: QueryDocumentSnapshot<Course>[]
  sx?: SxProps<Theme>
}) {
  if (courseSnapshots.length === 0) {
    return null
  }

  return <Box sx={sx}>
    <Typography variant="h6" component="h2" sx={{mb: 2, fontWeight: 700}}>
      {title}
    </Typography>
    <Grid container spacing={4} rowSpacing={4}>
      {courseSnapshots.map(courseSnapshot =>
        <CourseElement
          key={courseSnapshot.ref.path}
          courseSnapshot={courseSnapshot}
        />
      )}
    </Grid>
  </Box>
}

function CourseElement({courseSnapshot}: { courseSnapshot: QueryDocumentSnapshot<Course> }) {
  return <CourseContextProvider courseSnapshot={courseSnapshot}>
    <Grid size={{xs: 12, md: 4, sm: 6}}>
      <CourseCard/>
    </Grid>
  </CourseContextProvider>
}
